/**
 * Cohort-2-style 7-day itinerary, hardcoded for the planning calendar.
 * Built from the actual content in https://dcompass-sv.com/archives/2380
 * (real cohort: 14 university students, entrepreneurship focus,
 * September 2025) but structured for a generic "next cohort" the
 * pre-visit user (Sora) is preparing for.
 *
 * Names of guests follow the same rule as elsewhere: only mentioned by
 * name where the blog establishes them. Novel quotes are never put in
 * their mouths.
 */

export type EventType =
  | 'workshop'
  | 'interview'
  | 'visit'
  | 'lecture'
  | 'reflection'
  | 'social'
  | 'recreation'
  | 'travel';

export type TimeSlot = 'morning' | 'afternoon' | 'evening';

export interface ItineraryEvent {
  id: string;
  day: number; // 0-6
  slot: TimeSlot;
  title: string;
  type: EventType;
  description: string;
  /** Optional: hosts/guests/locations. */
  meta?: string;
}

export const TIME_SLOT_LABEL: Record<TimeSlot, string> = {
  morning: '午前',
  afternoon: '午後',
  evening: '夕方〜',
};

export const TIME_SLOT_ORDER: TimeSlot[] = ['morning', 'afternoon', 'evening'];

export const EVENT_TYPE_LABEL: Record<EventType, string> = {
  workshop: 'ワーク',
  interview: 'インタビュー',
  visit: '訪問',
  lecture: '講演',
  reflection: '振り返り',
  social: 'ソーシャル',
  recreation: '余暇',
  travel: '移動',
};

export const ITINERARY: ItineraryEvent[] = [
  // Day 0 — travel
  {
    id: 'd0-flight',
    day: 0,
    slot: 'afternoon',
    title: 'フライト・到着',
    type: 'travel',
    description: '日本→サンフランシスコ。SFO着、宿へ移動して時差調整。',
  },

  // Day 1
  {
    id: 'd1-mindblock',
    day: 1,
    slot: 'morning',
    title: 'マインドブロック解除ワーク',
    type: 'workshop',
    description: '挑戦を阻む思考・感情・固定観念を構造で見て、自分のブレーキを特定する。',
    meta: 'スタンフォード会場',
  },
  {
    id: 'd1-icebreak',
    day: 1,
    slot: 'afternoon',
    title: 'アイスブレイク + 自己紹介セッション',
    type: 'reflection',
    description: '参加者同士で「なぜここに来たか」を率直に共有する時間。',
  },

  // Day 2
  {
    id: 'd2-source',
    day: 2,
    slot: 'morning',
    title: '究極の目標と源泉ストーリー',
    type: 'workshop',
    description:
      '子ども時代から現在に至る「ずっと好きだったこと」を掘り下げ、Mission/Vision を仮置きする。',
  },
  {
    id: 'd2-pegasus',
    day: 2,
    slot: 'afternoon',
    title: 'Pegasus Tech Ventures 訪問・森本さん',
    type: 'interview',
    description:
      '大手企業 → スタンフォード MBA → スタートアップ → VC のキャリアと投資判断の話。',
    meta: 'Pegasus Tech Ventures オフィス',
  },

  // Day 3
  {
    id: 'd3-dschool',
    day: 3,
    slot: 'morning',
    title: 'd.school デザイン思考ワーク',
    type: 'workshop',
    description:
      'スタンフォード d.school の正規ワーク。多国籍チームでの問題解決を英語で実践。',
    meta: 'Stanford d.school',
  },
  {
    id: 'd3-sozo',
    day: 3,
    slot: 'afternoon',
    title: 'Sozo Venture 訪問・中村さん',
    type: 'interview',
    description: '世界的なベンチャーキャピタリスト、長期にわたるシリコンバレー視点。',
  },
  {
    id: 'd3-touchy',
    day: 3,
    slot: 'evening',
    title: 'Touchy Feely セッション',
    type: 'reflection',
    description:
      'スタンフォードの定番、深い対人ワーク。蓄積された信頼の上でやる脆弱性のシェア。',
  },

  // Day 4
  {
    id: 'd4-mendoza',
    day: 4,
    slot: 'morning',
    title: 'Mendoza さんと対話・エンジェル投資',
    type: 'interview',
    description: '創業 → IPO → エンジェル投資という連続のキャリアの話。',
  },
  {
    id: 'd4-kasagi',
    day: 4,
    slot: 'afternoon',
    title: '神戸市役所・笠木さんと対話',
    type: 'lecture',
    description:
      '商社 → 経済団体 → 自治体 という非典型的なキャリア。SVリエゾンの仕事。',
  },
  {
    id: 'd4-bbq',
    day: 4,
    slot: 'evening',
    title: 'BBQ ディナー',
    type: 'social',
    description:
      '教育者・連続起業家・トレーディング会社員・スタートアップ創業者らが混ざるカジュアルな場。',
  },

  // Day 5
  {
    id: 'd5-startup-prep',
    day: 5,
    slot: 'morning',
    title: 'スタートアップ・シミュレーション準備',
    type: 'workshop',
    description: '新規事業アイデアをチームで設計。「もし Uber がホスピタリティに入ったら」みたいなお題から。',
  },
  {
    id: 'd5-startup-pitch',
    day: 5,
    slot: 'afternoon',
    title: 'スタートアップ・シミュレーション発表',
    type: 'workshop',
    description: 'チームごとに発表。独自性と実装可能性が審査軸。',
  },

  // Day 6
  {
    id: 'd6-serial',
    day: 6,
    slot: 'morning',
    title: '連続起業家・上さん 講演',
    type: 'lecture',
    description: '複数の創業 + IPO 経験者の人生哲学。',
  },
  {
    id: 'd6-fukuhara',
    day: 6,
    slot: 'afternoon',
    title: 'Stanford GSE・福原さん',
    type: 'interview',
    description: 'AI を活用した教師支援サービスの研究。教育 × AI の最前線。',
    meta: 'Stanford Graduate School of Education',
  },
  {
    id: 'd6-closing',
    day: 6,
    slot: 'evening',
    title: '振り返り + 修了セレモニー',
    type: 'reflection',
    description: '1週間で見えたもの、これから持ち帰るものを言葉にする時間。',
  },
];

export function eventsByDay(day: number): ItineraryEvent[] {
  return ITINERARY.filter((e) => e.day === day).sort((a, b) => {
    const ai = TIME_SLOT_ORDER.indexOf(a.slot);
    const bi = TIME_SLOT_ORDER.indexOf(b.slot);
    return ai - bi;
  });
}

export const DAYS = [0, 1, 2, 3, 4, 5, 6];
